// Shared IndexedDB stores: settings, can-do progress, per-track session counters,
// task submissions. One database opened lazily per process.

import { openDB, type IDBPDatabase } from 'idb';
import type { CefrSubLevel } from './sample-today.ts';

export type Dialect = 'bokmal' | 'siddis';
export type Theme = 'auto' | 'light' | 'dark';

export interface Settings {
  dialect: Dialect;
  theme: Theme;
  dailyVocabLimit: number;
  dailyClozeLimit: number;
  dailyHvptLimit: number;
  dailyEcouteLimit: number;
  currentLevel: CefrSubLevel;
}

export const DEFAULT_SETTINGS: Settings = {
  dialect: 'bokmal',
  theme: 'auto',
  dailyVocabLimit: 20,
  dailyClozeLimit: 15,
  dailyHvptLimit: 12,
  dailyEcouteLimit: 8,
  currentLevel: 'A1.3',
};

export interface CanDoState {
  id: string;
  status: 'todo' | 'in-progress' | 'done';
  updatedAt: string;
}

export interface SessionLogEntry {
  /** ISO date YYYY-MM-DD */
  date: string;
  track: 'vocab' | 'cloze' | 'hvpt' | 'ecoute' | 'tache';
  /** Card / item count completed in this session bucket. */
  count: number;
  /** Average rating 0..1 if applicable (HVPT, cloze success rate). */
  score?: number;
}

export interface TaskSubmission {
  id: string;
  promptId: string;
  cefr: CefrSubLevel;
  mode: 'writing' | 'speaking';
  text: string;
  selfRating: { criterion: string; met: boolean }[];
  submittedAt: string;
}

const DB_NAME = 'parlenorvegien';
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1 && !db.objectStoreNames.contains('fsrs-cards')) {
        db.createObjectStore('fsrs-cards');
      }
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings');
        if (!db.objectStoreNames.contains('candos')) db.createObjectStore('candos', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('sessions')) {
          const s = db.createObjectStore('sessions', { autoIncrement: true });
          s.createIndex('byDate', 'date');
          s.createIndex('byTrack', 'track');
        }
        if (!db.objectStoreNames.contains('tasks')) db.createObjectStore('tasks', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('cloze-state')) db.createObjectStore('cloze-state');
        if (!db.objectStoreNames.contains('hvpt-state')) db.createObjectStore('hvpt-state');
      }
    },
  });
  return dbPromise;
}

// --- Settings ---

export async function loadSettings(): Promise<Settings> {
  const db = await getDB();
  const raw = await db.get('settings', 'app') as Partial<Settings> | undefined;
  return { ...DEFAULT_SETTINGS, ...(raw ?? {}) };
}

export async function saveSettings(s: Settings): Promise<void> {
  const db = await getDB();
  await db.put('settings', s, 'app');
  notifySettingsChange(s);
}

type SettingsListener = (s: Settings) => void;
const settingsListeners = new Set<SettingsListener>();

export function onSettingsChange(listener: SettingsListener): () => void {
  settingsListeners.add(listener);
  return () => settingsListeners.delete(listener);
}

function notifySettingsChange(s: Settings): void {
  for (const l of settingsListeners) l(s);
}

// --- Can-do progress ---

export async function loadCanDoStates(): Promise<Map<string, CanDoState>> {
  const db = await getDB();
  const all = await db.getAll('candos') as CanDoState[];
  return new Map(all.map((c) => [c.id, c]));
}

export async function setCanDoStatus(id: string, status: CanDoState['status']): Promise<void> {
  const db = await getDB();
  await db.put('candos', { id, status, updatedAt: new Date().toISOString() });
}

// --- Session log ---

export function isoDate(d = new Date()): string {
  return d.toISOString().slice(0, 10);
}

export async function logSession(entry: Omit<SessionLogEntry, 'date'> & { date?: string }): Promise<void> {
  const db = await getDB();
  await db.add('sessions', { ...entry, date: entry.date ?? isoDate() });
}

export async function sessionsForDate(date: string): Promise<SessionLogEntry[]> {
  const db = await getDB();
  return await db.getAllFromIndex('sessions', 'byDate', date) as SessionLogEntry[];
}

export async function sessionsForTrack(track: SessionLogEntry['track']): Promise<SessionLogEntry[]> {
  const db = await getDB();
  return await db.getAllFromIndex('sessions', 'byTrack', track) as SessionLogEntry[];
}

export async function allSessions(): Promise<SessionLogEntry[]> {
  const db = await getDB();
  return await db.getAll('sessions') as SessionLogEntry[];
}

// --- Task submissions ---

export async function saveTaskSubmission(t: TaskSubmission): Promise<void> {
  const db = await getDB();
  await db.put('tasks', t);
}

export async function loadTaskSubmissions(): Promise<TaskSubmission[]> {
  const db = await getDB();
  return await db.getAll('tasks') as TaskSubmission[];
}

// --- Per-card item state (cloze, hvpt) ---

export async function getItemState<T>(store: 'cloze-state' | 'hvpt-state', id: string): Promise<T | undefined> {
  const db = await getDB();
  return await db.get(store, id) as T | undefined;
}

export async function putItemState<T>(store: 'cloze-state' | 'hvpt-state', id: string, value: T): Promise<void> {
  const db = await getDB();
  await db.put(store, value, id);
}

export async function getAllItemStates<T>(store: 'cloze-state' | 'hvpt-state'): Promise<Map<string, T>> {
  const db = await getDB();
  const result = new Map<string, T>();
  const tx = db.transaction(store);
  for await (const cursor of tx.store) {
    result.set(cursor.key as string, cursor.value as T);
  }
  return result;
}

// --- FSRS cards (vocab) ---

export async function getFsrsState<T>(id: string): Promise<T | undefined> {
  const db = await getDB();
  return await db.get('fsrs-cards', id) as T | undefined;
}

export async function putFsrsState<T>(id: string, value: T): Promise<void> {
  const db = await getDB();
  await db.put('fsrs-cards', value, id);
}

export async function allFsrsStates<T>(): Promise<Map<string, T>> {
  const db = await getDB();
  const result = new Map<string, T>();
  const tx = db.transaction('fsrs-cards');
  for await (const cursor of tx.store) {
    result.set(cursor.key as string, cursor.value as T);
  }
  return result;
}

// --- Reset (Paramètres) ---

export async function resetAllData(): Promise<void> {
  const db = await getDB();
  const stores: ('fsrs-cards' | 'settings' | 'candos' | 'sessions' | 'tasks' | 'cloze-state' | 'hvpt-state')[] = [
    'fsrs-cards', 'settings', 'candos', 'sessions', 'tasks', 'cloze-state', 'hvpt-state',
  ];
  await Promise.all(stores.map((s) => db.clear(s)));
  notifySettingsChange(DEFAULT_SETTINGS);
}

// --- Export (Paramètres) ---

export async function exportAllData(): Promise<Record<string, unknown>> {
  const db = await getDB();
  const [settings, candos, sessions, tasks, fsrs, cloze, hvpt] = await Promise.all([
    db.get('settings', 'app'),
    db.getAll('candos'),
    db.getAll('sessions'),
    db.getAll('tasks'),
    (async () => {
      const out: Record<string, unknown> = {};
      const tx = db.transaction('fsrs-cards');
      for await (const cursor of tx.store) out[cursor.key as string] = cursor.value;
      return out;
    })(),
    (async () => {
      const out: Record<string, unknown> = {};
      const tx = db.transaction('cloze-state');
      for await (const cursor of tx.store) out[cursor.key as string] = cursor.value;
      return out;
    })(),
    (async () => {
      const out: Record<string, unknown> = {};
      const tx = db.transaction('hvpt-state');
      for await (const cursor of tx.store) out[cursor.key as string] = cursor.value;
      return out;
    })(),
  ]);
  return {
    exportedAt: new Date().toISOString(),
    version: DB_VERSION,
    settings, candos, sessions, tasks,
    'fsrs-cards': fsrs, 'cloze-state': cloze, 'hvpt-state': hvpt,
  };
}
